import { type DeadlineRecurrenceChoice, recurrenceChoiceOf } from '@prospero/shared';

export interface DeadlineRecurrenceValue {
  recurrence: DeadlineRecurrenceChoice | undefined;
  /** YYYY-MM-DD */
  dueAt: string | undefined;
  validityMonths: number | undefined;
}

/** Valore iniziale del dialog di modifica a partire dalla riga; la risposta viene da `recurrenceChoiceOf`. */
export function recurrenceOfDeadline(row: {
  dueAt: string | null;
  validityMonths: number | null;
}): DeadlineRecurrenceValue {
  return {
    recurrence: recurrenceChoiceOf(row),
    dueAt: row.dueAt ?? undefined,
    validityMonths: row.validityMonths ?? undefined,
  };
}

/** Cambio risposta: NONE azzera data e validità; ONE_OFF azzera la validità; RECURRING la valorizza (attuale ?? suggerita). */
export function applyRecurrenceChoice(
  current: DeadlineRecurrenceValue,
  next: DeadlineRecurrenceChoice,
  suggestedMonths: number | null,
): DeadlineRecurrenceValue {
  switch (next) {
    case 'NONE':
      return { recurrence: 'NONE', dueAt: undefined, validityMonths: undefined };
    case 'ONE_OFF':
      return { recurrence: 'ONE_OFF', dueAt: current.dueAt, validityMonths: undefined };
    case 'RECURRING':
      return {
        recurrence: 'RECURRING',
        dueAt: current.dueAt,
        validityMonths: current.validityMonths ?? suggestedMonths ?? undefined,
      };
  }
}

export function toCreateRecurrencePayload(value: DeadlineRecurrenceValue): {
  dueAt?: string;
  validityMonths?: number;
} {
  if (value.recurrence === 'ONE_OFF') {
    return { dueAt: value.dueAt };
  }
  if (value.recurrence === 'RECURRING') {
    return { dueAt: value.dueAt, validityMonths: value.validityMonths };
  }
  return {};
}

/** Il preavviso appartiene alla risposta "ha una scadenza": prima della risposta non si chiede. */
export function asksWarningDays(recurrence: DeadlineRecurrenceChoice | undefined): boolean {
  return recurrence === 'ONE_OFF' || recurrence === 'RECURRING';
}

/** Senza data non c'è nulla da avvisare: il campo è nascosto e i giorni non viaggiano. */
export function toWarningDaysPayload(
  recurrence: DeadlineRecurrenceChoice | undefined,
  warningDays: number[] | undefined,
): { warningDays?: number[] } {
  return recurrence !== 'NONE' && warningDays && warningDays.length > 0 ? { warningDays } : {};
}

export function toUpdateRecurrencePayload(value: DeadlineRecurrenceValue): {
  dueAt: string | null;
  validityMonths: number | null;
} {
  return {
    dueAt: value.recurrence === 'NONE' ? null : (value.dueAt ?? null),
    validityMonths: value.recurrence === 'RECURRING' ? (value.validityMonths ?? null) : null,
  };
}
